import { IAddInsuredObjects, IBaseSoldPolicy, IHealthSoldPolicy, IMotorSoldPolicy, IPropertySoldPoliy, IRemoveInsuredObjects, IReturnedPremiumPolicy, IStock, Result } from '@/types/base';
import {
    AgGridRequest,
    ApproveDraftPolicy_Request,
    CancelPolicyCommand_Request,
    CustomerModel,
    GetHealthPolicyGridExportedQuery_Request,
    GetHealthPolicyGridQuery_Request,
    GetMarinePolicyGridExportedQuery_Request,
    GetMarinePolicyGridQuery_Request,
    GetMotorPolicyGridExportedQuery_Request,
    GetMotorPolicyGridQuery_Request,
    GetPropertyPolicyGridExportedQuery_Request,
    GetPropertyPolicyGridQuery_Request,
    ImportRow,
    IssueRequest,
} from '@/types/models';
import { IBusinessRules } from '@/types/sale';
import moment, { Moment } from 'moment';
import baseParams from './baseParams';
import request, { request_v2 } from './request';

// Skips the global error toast, the caller handles the error itself
const silentErrorHandler = (error: unknown) => {
    throw error;
};

class Policy {
    public static async getSerialIntervals(productId: string): Promise<Result<IStock[]>> {
        return request(`/policy/intervals/${productId}`);
    }

    public static getPolicyPreview(department: string, productId: string, productName: string): string {
        return `${API_HOST}/sales/${department}/${productId}/${productName}/preview`;
    }

    public static getPolicyTerms(productId: string): string {
        return `${API_HOST}/policy/gettermsandconditions/${productId}`;
    }

    public static getPolicyPrintPreview(department: string, productId: string, productName: string): string {
        return `${API_HOST}/sales/${department}/${productId}/${productName}/print-preview`;
    }

    public static async getSinglePolicy(id: string): Promise<Result<any>> {
        return request(`/policy/${id}`);
    }

    public static async getSinglePolicyImport(id: string): Promise<Result<ImportRow>> {
        return request(`/import/${id}`);
    }

    public static async importPolicyOnEsig(id: string): Promise<Result<ImportRow>> {
        return request(`/import/import-policy-log-by-id/${id}`, {
            method: 'POST',
            data: {},
        });
    }

    public static async updatePolicyCustomers(id: string, data: any): Promise<Result<any>> {
        return request(`/import/update-policy-log-customers-by-id/${id}`, {
            method: 'POST',
            data,
        });
    }

    public static async cancelPolicy(policyId: string, reason: string): Promise<Result<null>> {
        return request(`/policy/cancel/${policyId}`, {
            method: 'POST',
            data: { reason } as CancelPolicyCommand_Request,
        });
    }

    public static async returnedPremiumPolicy(policyId: string, data: IReturnedPremiumPolicy, endpoint: string): Promise<Result<any>> {
        return request(`/policystandard/${endpoint}/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async returnedPolicyAg(policyId: string, data: AgGridRequest): Promise<Result<any>> {
        return request(`/policystandard/return/${policyId}/ag`, {
            method: 'POST',
            data,
        });
    }

    public static async cmrTransportAg(policyId: string, data: AgGridRequest): Promise<Result<any>> {
        return request(`/policy/marine/cmr-transport/${policyId}/ag`, {
            method: 'POST',
            data,
        });
    }

    // Print Command
    public static printCommand(id: string): string {
        return `${API_HOST}/policystandard/return/print/${id}`;
    }

    public static printCommandBefore(id: string): string {
        return `${API_HOST}/policystandard/return/print/premium/${id}`;
    }

    public static async getAvailableInsurers(productId: string, isCrossSelling: boolean): Promise<Result<any>> {
        return request(`/policy/getavailableinsurers/${productId}`, {
            method: 'POST',
            data: { isCrossSelling },
        });
    }

    public static async getMotorPoliciesAg(params: GetMotorPolicyGridQuery_Request): Promise<Result<IMotorSoldPolicy[]>> {
        return request('/policy/motor/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async getMotorPoliciesAg_v2(params: GetMotorPolicyGridQuery_Request): Promise<Result<IMotorSoldPolicy[]>> {
        return request_v2('/policy/motor/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async exportMotorPoliciesAg(data: GetMotorPolicyGridExportedQuery_Request): Promise<BlobPart> {
        return request(`/policy/motor/export/ag`, {
            method: 'POST',
            data,
            responseType: 'blob',
        });
    }

    public static async exportMotorPoliciesAg_v2(data: GetMotorPolicyGridExportedQuery_Request): Promise<BlobPart> {
        return request_v2(`/policy/motor/export/ag`, {
            method: 'POST',
            data,
            responseType: 'blob',
        });
    }

    public static async getHealthPoliciesAg(params: GetHealthPolicyGridQuery_Request): Promise<Result<IHealthSoldPolicy[]>> {
        return request('/policy/health/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async exportHealthPoliciesAg(data: GetHealthPolicyGridExportedQuery_Request): Promise<BlobPart> {
        return request(`/policy/health/export/ag`, {
            method: 'POST',
            data,
            responseType: 'blob',
        });
    }

    public static async reinsuranceExcel(productName: 'antitumor' | 'life-plus', body: { issuedDateFrom: Moment; issuedDateTo: Moment }): Promise<BlobPart> {
        const data = {
            issuedDateFrom: moment(body.issuedDateFrom).startOf('day').toISOString(),
            issuedDateTo: moment(body.issuedDateTo).startOf('day').toISOString(),
        };

        return request(`/sales/health/${productName}-reinsurance-excel`, {
            method: 'POST',
            data: data,
            responseType: 'blob',
        });
    }

    public static async getPoliciesImport(params: AgGridRequest): Promise<Result<ImportRow[]>> {
        return request('/import/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async policyPreview(sector: string, policyId: string, product: string): Promise<BlobPart> {
        return request(`/sales/${sector}/${policyId}/${product}/preview`, {
            method: 'GET',
            responseType: 'blob',
        });
    }

    public static async getRenewalItems(id: string): Promise<Result<{ renewalItems: any[] }>> {
        return request(`/renewal/${id}`);
    }

    public static async getPropertyPoliciesAg(params: GetPropertyPolicyGridQuery_Request): Promise<Result<IPropertySoldPoliy[]>> {
        return request('/policy/property/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async exportPropertyPoliciesAg(data: GetPropertyPolicyGridExportedQuery_Request): Promise<BlobPart> {
        return request(`/policy/property/export/ag`, {
            method: 'POST',
            data,
            responseType: 'blob',
        });
    }

    public static async getMarinePoliciesAg(params: GetMarinePolicyGridQuery_Request): Promise<Result<IBaseSoldPolicy[]>> {
        return request('/policy/marine/ag', {
            method: 'POST',
            data: params,
        });
    }

    public static async approveDraftPolicy(policyId: string, data: ApproveDraftPolicy_Request): Promise<Result<any>> {
        return request(`/policystandard/approve-draft/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async refuseDraftPolicy(policyId: string, reason: string): Promise<Result<any>> {
        return request(`/policystandard/refuse-draft/${policyId}`, {
            method: 'POST',
            data: { reason },
        });
    }

    public static async exportMarinePoliciesAg(data: GetMarinePolicyGridExportedQuery_Request): Promise<BlobPart> {
        return request(`/policy/marine/export/ag`, {
            method: 'POST',
            data,
            responseType: 'blob',
        });
    }

    public static async uploadDocument(document: FormData): Promise<Result<{ id: string }>> {
        return request(`/document/upload`, {
            method: 'POST',
            data: document,
        });
    }

    public static async downloadDocument(documentId: string): Promise<BlobPart> {
        return request(`/document/download/${documentId}`, {
            method: 'GET',
            responseType: 'blob',
        });
    }

    public static async getDocumentsAg(policyId: string): Promise<Result<any>> {
        return request(`/document/ag`, {
            method: 'POST',
            data: {
                ...baseParams,
                policyId,
            },
        });
    }

    public static async attachDocuments(policyId: string, documentIds: string[]): Promise<Result<any>> {
        return request(`/policy/attach-documents/${policyId}`, {
            method: 'POST',
            data: { documentIds },
        });
    }

    public static async duplicatePremium(policyId: string, data: any): Promise<Result<any>> {
        return request(`/policystandard/duplicate_v2/premium/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async annexPremium(policyId: string, data: any): Promise<Result<any>> {
        return request(`/policystandard/annex/premium/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async finalizeAnnex(policyId: string, data: any): Promise<Result<any>> {
        return request(`/policystandard/annex/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async policyCardExport(policyId: any): Promise<BlobPart> {
        return request(`/policy/card/export/ag`, {
            method: 'POST',
            data: {
                policyId,
                exportFormat: 'Xlsx',
            },
            responseType: 'blob',
        });
    }

    public static async finalizeDuplication(policyId: string, data: any): Promise<Result<any>> {
        return request(`/policystandard/duplicate_v2/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async duplicateBusinessRules(productId: string): Promise<Result<IBusinessRules>> {
        return request(`/policystandard/business-rules`, {
            method: 'POST',
            data: {
                productId,
                metadata: {},
            },
        });
    }

    public static async duplicatePresale(productId: string): Promise<Result<IssueRequest>> {
        return request(`/policystandard/presale`, {
            method: 'POST',
            data: {
                productId,
                metadata: {},
            },
        });
    }

    public static async duplicateVehicleDetails(plate: string, vin: string, productId: string) {
        return request('/sales/motor/vehicle-details', {
            method: 'POST',
            data: {
                plate,
                vin,
                productId,
                metadata: {},
            },
        });
    }

    public static async duplicateListAg(params: AgGridRequest, serial: string, identifier2: string, identifier: string, insuranceSectorType: 'Health' | 'Marine' | 'Motor' | 'Other' | 'Property') {
        return request('/policy/forduplicate/ag', {
            method: 'POST',
            data: {
                ...params,
                identifier,
                identifier2,
                serial,
                insuranceSectorType,
            },
        });
    }

    public static async updateInstallment(installmentId: string, data: { grossPremium: number; sumInsured: number }) {
        return request(`/installment/issue/${installmentId}`, {
            method: 'POST',
            data,
        });
    }

    public static printHealthCards(policyId: string, policyUserId?: string) {
        return `${API_HOST}/policystandard/print/cards/${policyId}${policyUserId ? `?policyUserId=${policyUserId}` : ''}`;
    }

    public static printCmrTransport(transportId: string, language: 'AL' | 'EN' = 'AL') {
        return `${API_HOST}/policystandard/cmr-transport/print/${transportId}/${language}`;
    }

    public static async addHealthClients(policyId: string, data: IAddInsuredObjects): Promise<Result<any>> {
        return request(`/policystandard/mid-term/add-insured-object/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async removeHealthClients(policyId: string, data: IRemoveInsuredObjects): Promise<Result<any>> {
        return request(`/policystandard/mid-term/remove-insured-object/${policyId}`, {
            method: 'POST',
            data,
        });
    }

    public static async getPolicyParticipants(
        serial: string,
        productId: string
    ): Promise<
        Result<{
            beneficiaries: CustomerModel[];
            policyHolders: CustomerModel[];
            invoiceRecipients: CustomerModel[];
            policyUsers: CustomerModel[];
        }>
    > {
        return request(`/policyparticipants?Serial=${serial}&ProductId=${productId}`, {
            method: 'GET',
        });
    }

    public static async checkFailedInvoices(): Promise<
        Result<{
            failedInvoicesCount: number;
            isSaleAllowed: boolean;
        }>
    > {
        return request(`/finance/check-failed-invoices`, {
            method: 'GET',
        });
    }

    public static async getSystemInfo(): Promise<
        Result<{
            environment: string;
            version: string;
            name: string;
        }>
    > {
        return request(`/system/info`, {
            method: 'GET',
        });
    }

    public static async registerSerial(productId: string, serial: string): Promise<Result<string>> {
        return request('/policystandard/serials/register', { method: 'POST', data: { productId, serial }, errorHandler: silentErrorHandler });
    }

    public static async getReservedSerials(productId: string): Promise<Result<string[]>> {
        return request(`/policystandard/serials/${productId}`, { errorHandler: silentErrorHandler });
    }
}

export default Policy;
